import PropTypes from "prop-types";
import { wrap } from "lodash";
import moment from "moment";

export const DataSource = PropTypes.shape({
  syntax: PropTypes.string,
  options: PropTypes.shape({
    doc: PropTypes.string,
    doc_url: PropTypes.string,
  }),
  type_name: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  paused: PropTypes.number,
  pause_reason: PropTypes.string,
  icon: PropTypes.string,
});

export const Table = PropTypes.shape({
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
});

export const Schema = PropTypes.arrayOf(Table);

export const RefreshScheduleType = PropTypes.shape({
  interval: PropTypes.number,
  time: PropTypes.string,
  day_of_week: PropTypes.string,
  until: PropTypes.string,
});

export const RefreshScheduleDefault = {
  interval: null,
  time: null,
  day_of_week: null,
  until: null,
};

export const UserProfile = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  profileImageUrl: PropTypes.string,
  apiKey: PropTypes.string,
  isDisabled: PropTypes.bool,
});

export const Destination = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
});

export const Query = PropTypes.shape({
  id: PropTypes.any.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  data_source_id: PropTypes.any.isRequired,
  created_at: PropTypes.string.isRequired,
  updated_at: PropTypes.string,
  user: UserProfile,
  query: PropTypes.string,
  queryHash: PropTypes.string,
  is_safe: PropTypes.bool.isRequired,
  is_draft: PropTypes.bool.isRequired,
  is_archived: PropTypes.bool.isRequired,
  api_key: PropTypes.string.isRequired,
});

export const AlertOptions = PropTypes.shape({
  column: PropTypes.string,
  op: PropTypes.oneOf([">", ">=", "<", "<=", "==", "!="]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  custom_subject: PropTypes.string,
  custom_body: PropTypes.string,
});

export const Alert = PropTypes.shape({
  id: PropTypes.any,
  name: PropTypes.string,
  created_at: PropTypes.string,
  last_triggered_at: PropTypes.string,
  updated_at: PropTypes.string,
  rearm: PropTypes.number,
  state: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  user: UserProfile,
  query: Query,
  is_archived: PropTypes.bool.isRequired,
  options: PropTypes.shape({
    column: PropTypes.string,
    op: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
});

export const AlertEvent = PropTypes.shape({
  id: PropTypes.any,
  name: PropTypes.string,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
  alert: Alert,
  query: Query,
  user: UserProfile,
  destination: Destination,
  is_favorite: PropTypes.bool,
  is_archived: PropTypes.bool.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
});

export const ModelOptions = PropTypes.shape({
  column: PropTypes.string,
  op: PropTypes.oneOf([">", ">=", "<", "<=", "==", "!="]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  custom_subject: PropTypes.string,
  custom_body: PropTypes.string,
  random_state: PropTypes.number,
  train_size: PropTypes.number,
  test_size: PropTypes.number,
  regressor: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
  categories: PropTypes.arrayOf(PropTypes.string),
  targets: PropTypes.arrayOf(PropTypes.string),
  timestamp: PropTypes.string,
});

export const MLModel = PropTypes.shape({
  id: PropTypes.any,
  name: PropTypes.string,
  version: PropTypes.number,
  created_at: PropTypes.string,
  last_triggered_at: PropTypes.string,
  updated_at: PropTypes.string,
  rearm: PropTypes.number,
  state: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  state_train: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  state_predict: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  user: UserProfile,
  query: Query,
  tags: PropTypes.arrayOf(PropTypes.string),
  is_archived: PropTypes.bool.isRequired,
  options: PropTypes.shape({
    column: PropTypes.string,
    op: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    random_state: PropTypes.number,
    train_size: PropTypes.number,
    test_size: PropTypes.number,
    regressor: PropTypes.string,
    regressor_options: PropTypes.object,
    features: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
    targets: PropTypes.arrayOf(PropTypes.string),
    timestamp: PropTypes.string,
  }).isRequired,
});

export const MLModelVersion = PropTypes.shape({
  id: PropTypes.any,
  model_id: PropTypes.any,
  model: MLModel,
  name: PropTypes.string,
  version: PropTypes.number,
  created_at: PropTypes.string,
  last_triggered_at: PropTypes.string,
  updated_at: PropTypes.string,
  rearm: PropTypes.number,
  state: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  state_train: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  state_predict: PropTypes.oneOf(["ok", "triggered", "unknown", "training", "trained", "predicting", "predicted"]),
  user: UserProfile,
  query: Query,
  changes: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  is_archived: PropTypes.bool.isRequired,
  input_data: PropTypes.any,
  options: PropTypes.shape({
    column: PropTypes.string,
    op: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    random_state: PropTypes.number,
    train_size: PropTypes.number,
    test_size: PropTypes.number,
    regressor: PropTypes.string,
    regressor_options: PropTypes.object,
    features: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
    targets: PropTypes.arrayOf(PropTypes.string),
    timestamp: PropTypes.string,
  }).isRequired,
});

export const PredictionResult = PropTypes.shape({
  id: PropTypes.any,
  name: PropTypes.string,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
  model: MLModel,
  query: Query,
  user: UserProfile,
  destination: Destination,
  is_favorite: PropTypes.bool,
  is_archived: PropTypes.bool.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
  input_data: PropTypes.any,
  content: PropTypes.any,
  additional_properties: PropTypes.object,
});

export const Indexer = PropTypes.shape({
  id: PropTypes.any,
  name: PropTypes.string,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
  last_run_at: PropTypes.string,
  user: UserProfile,
  query: Query,
  data_source: DataSource,
  is_archived: PropTypes.bool,
  is_favorite: PropTypes.bool,
  tags: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.shape({
    data_source_id: PropTypes.number,
    target_table: PropTypes.string,
    delete_duplicates: PropTypes.bool,
    insert_strategy: PropTypes.string,
  }),
});

export const IndexerOptions = PropTypes.shape({
  data_source_id: PropTypes.number,
  target_table: PropTypes.string,
  delete_duplicates: PropTypes.bool,
  insert_strategy: PropTypes.string,
});

function checkMoment(isRequired, props, propName, componentName) {
  const value = props[propName];
  const isRequiredValid = isRequired && value !== null && value !== undefined && moment.isMoment(value);
  const isOptionalValid = !isRequired && (value === null || value === undefined || moment.isMoment(value));
  if (!isRequiredValid && !isOptionalValid) {
    return new Error("Prop `" + propName + "` supplied to `" + componentName + "` should be a Moment.js instance.");
  }
}

export const Moment = wrap(false, checkMoment);
Moment.isRequired = wrap(true, checkMoment);
